import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { Link } from 'react-router-dom';

import type { OrganizationDetail } from '@/types/organization';
import { Pill } from '@/ui';
import type { GridFieldConfig } from '@/utils/gridFilters';

// Mirrors backend/homebase/dugout/filters/organization_filter.py.
const TEXT_FIELDS = ['name', 'primary_email', 'internal_owner', 'kustomer_id', 'description'];
const DATE_FIELDS = ['created_at', 'updated_at'];

export const ORGANIZATION_GRID_FIELDS: GridFieldConfig = {
  text: new Set(TEXT_FIELDS),
  date: new Set(DATE_FIELDS),
  number: new Set(),
  blank: new Set(TEXT_FIELDS),
  sortable: new Set([...TEXT_FIELDS, ...DATE_FIELDS, 'is_visible_to_public'])
};

export const DEFAULT_ORDERING = 'name';

// The server filters these timestamps by UTC calendar day
// (organization_filter.py), so the grid shows and re-filters them by UTC day
// too -- otherwise a row near midnight can pass one side and not the other.
const utcDay = (value: string) => {
  const date = new Date(value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const dateFilterParams = {
  // Only the operators translateFieldCondition sends to the server -- any
  // other would filter just the fetched page, hiding matches on other pages.
  filterOptions: ['inRange', 'greaterThan', 'lessThan'],
  inRangeInclusive: true,
  maxNumConditions: 1,
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string | null) => {
    if (!cellValue) return -1;
    const cellDay = utcDay(cellValue);
    // The picked calendar date, as a UTC day.
    const filterDay = Date.UTC(
      filterLocalDateAtMidnight.getFullYear(),
      filterLocalDateAtMidnight.getMonth(),
      filterLocalDateAtMidnight.getDate()
    );
    if (cellDay === filterDay) return 0;
    return cellDay < filterDay ? -1 : 1;
  }
};

const formatDate = (params: ValueFormatterParams<OrganizationDetail, string>) =>
  params.value ? new Date(params.value).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '';

const nameCellRenderer = (params: ICellRendererParams<OrganizationDetail>) => {
  const organization = params.data;
  if (!organization) return '';
  return (
    <Link
      to={organization.id}
      className="text-uniform-primary-600 hover:underline dark:text-uniform-primary-400">
      {organization.name}
    </Link>
  );
};

const primaryEmailCellRenderer = (params: ICellRendererParams<OrganizationDetail>) => {
  if (params.data?.primary_email) return params.data.primary_email;
  return (
    <Pill size="sm" variant="subtle" color="warning" className="inline leading-none">
      No email
    </Pill>
  );
};

export const createColumnDefs = (): ColDef<OrganizationDetail>[] => [
  { field: 'name', headerName: 'Name', flex: 2, cellRenderer: nameCellRenderer },
  {
    field: 'primary_email',
    headerName: 'Default email (ROI)',
    flex: 2,
    cellRenderer: primaryEmailCellRenderer
  },
  { field: 'internal_owner', headerName: 'Internal owner', flex: 1 },
  {
    field: 'is_visible_to_public',
    headerName: 'Public',
    cellDataType: 'boolean',
    width: 110,
    flex: 0
  },
  { field: 'kustomer_id', headerName: 'Kustomer ID', flex: 1 },
  { field: 'description', headerName: 'Description', flex: 2 },
  {
    field: 'created_at',
    headerName: 'Created (UTC)',
    cellDataType: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueFormatter: formatDate,
    flex: 1
  },
  {
    field: 'updated_at',
    headerName: 'Updated (UTC)',
    cellDataType: false,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueFormatter: formatDate,
    flex: 1
  }
];
